"use client";

import { teamService } from "@/lib/services";
import { TeamMember } from "@/lib/types";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import { useEffect, useState } from "react";

// Skeleton Loader for Team Member
function TeamMemberSkeleton() {
	return (
		<div className="animate-pulse">
			<div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-8 items-center">
				<div className="w-64 h-64 mx-auto md:mx-0 bg-gray-300 rounded-full" />
				<div>
					<div className="h-8 bg-gray-300 rounded w-3/4 mb-4" />
					<div className="h-6 bg-gray-300 rounded w-1/2 mb-4" />
					<div className="space-y-2">
						<div className="h-4 bg-gray-300 rounded" />
						<div className="h-4 bg-gray-300 rounded w-5/6" />
						<div className="h-4 bg-gray-300 rounded w-4/6" />
					</div>
				</div>
			</div>
			<div className="flex justify-center gap-4 mt-8">
				<div className="w-12 h-12 rounded-full bg-gray-300" />
				<div className="w-12 h-12 rounded-full bg-gray-300" />
			</div>
		</div>
	);
}

// Empty State
function EmptyState() {
	return (
		<div className="text-center py-12">
			<div className="max-w-md mx-auto">
				<div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
					<svg
						className="w-10 h-10 text-gray-400"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
						/>
					</svg>
				</div>
				<h3 className="text-xl font-bold text-gray-900 mb-2">
					No Team Members Yet
				</h3>
				<p className="text-gray-600">Check back later to meet the RFFA team.</p>
			</div>
		</div>
	);
}

// Error State
function ErrorState({ onRetry }: { onRetry: () => void }) {
	return (
		<div className="text-center py-12">
			<div className="max-w-md mx-auto">
				<div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
					<svg
						className="w-10 h-10 text-red-500"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
						/>
					</svg>
				</div>
				<h3 className="text-xl font-bold text-gray-900 mb-2">
					Oops! Something went wrong
				</h3>
				<p className="text-gray-600 mb-4">
					We couldn&apos;t load the team members.
				</p>
				<button
					onClick={onRetry}
					className="px-6 py-2 bg-primary hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
				>
					Try Again
				</button>
			</div>
		</div>
	);
}

export default function TeamMembers() {
	const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [currentMember, setCurrentMember] = useState(0);

	const fetchTeamMembers = async () => {
		setLoading(true);
		setError(false);

		try {
			const response = await teamService.getAll();

			if (response.success && response.data) {
				setTeamMembers(response.data);
			} else {
				setError(true);
			}
		} catch (err) {
			console.error("Error fetching team members:", err);
			setError(true);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchTeamMembers();
	}, []);

	const nextMember = () => {
		setCurrentMember((prev) => (prev + 1) % teamMembers.length);
	};

	const prevMember = () => {
		setCurrentMember((prev) =>
			prev === 0 ? teamMembers.length - 1 : prev - 1
		);
	};

	const member = teamMembers[currentMember];

	return (
		<section className="py-16 md:py-24 bg-white">
			<div className="container mx-auto px-4">
				<h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 text-center mb-12 uppercase">
					our SPORTS team
				</h2>

				<div className="max-w-5xl mx-auto">
					{/* Loading State */}
					{loading && <TeamMemberSkeleton />}

					{/* Error State */}
					{error && !loading && <ErrorState onRetry={fetchTeamMembers} />}

					{/* Empty State */}
					{!loading && !error && teamMembers.length === 0 && <EmptyState />}

					{/* Success State with Data */}
					{!loading && !error && teamMembers.length > 0 && (
						<>
							<div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-8 items-start">
								{/* Team Member Image */}
								<div className="relative w-64 h-64 mx-auto md:mx-0">
									<Image
										src={member.image_url || "/assets/team/placeholder.png"}
										alt={member.name}
										fill
										className="object-cover rounded-full"
									/>
								</div>

								{/* Team Member Info */}
								<div>
									<div className="border-l-8 border-primary pl-4">
										<h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
											{member.name}
										</h3>
										<p className="text-primary font-bold text-lg mb-4">
											{member.title}
										</p>
									</div>
									<p className="text-gray-600 leading-relaxed pl-4">
										{member.description}
									</p>
								</div>
							</div>

							{/* Navigation Arrows */}
							{teamMembers.length > 1 && (
								<div className="flex justify-center gap-4 mt-8">
									<button
										onClick={prevMember}
										className="w-12 h-12 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-primary hover:text-primary transition-colors"
										aria-label="Previous team member"
									>
										<ChevronLeft className="w-6 h-6" />
									</button>
									<button
										onClick={nextMember}
										className="w-12 h-12 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-primary hover:text-primary transition-colors"
										aria-label="Next team member"
									>
										<ChevronRight className="w-6 h-6" />
									</button>
								</div>
							)}

							{/* Team Member Counter */}
							<div className="text-center text-gray-600 text-sm mt-4">
								{currentMember + 1} of {teamMembers.length}
							</div>
						</>
					)}
				</div>
			</div>
		</section>
	);
}
